import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import { ArrowLeft, Plus, X, Phone, Mail, Calendar, User, DollarSign, MessageCircle, MessageSquare, MoreHorizontal } from "lucide-react";

const CONTACT_METHODS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  WHATSAPP: { label: "WhatsApp", color: "bg-green-100 text-green-700 border border-green-200",   icon: <MessageCircle size={13}/> },
  CALL:     { label: "Call",     color: "bg-blue-100 text-blue-700 border border-blue-200",      icon: <Phone size={13}/> },
  CHAT:     { label: "Chat",     color: "bg-purple-100 text-purple-700 border border-purple-200",icon: <MessageSquare size={13}/> },
  EMAIL:    { label: "Email",    color: "bg-yellow-100 text-yellow-700 border border-yellow-200",icon: <Mail size={13}/> },
  OTHER:    { label: "Other",    color: "bg-gray-100 text-gray-700 border border-gray-200",      icon: <MoreHorizontal size={13}/> },
};

const STATUS_OPTIONS = [
  { value: "NEW_LEAD", label: "New Lead", color: "bg-gray-100 text-gray-700" },
  { value: "INTERESTED", label: "Interested", color: "bg-blue-100 text-blue-700" },
  { value: "FOLLOW_UP_REQUIRED", label: "Follow-Up Required", color: "bg-yellow-100 text-yellow-700" },
  { value: "NEGOTIATION", label: "Negotiation", color: "bg-orange-100 text-orange-700" },
  { value: "POTENTIAL_BUYER", label: "Potential Buyer", color: "bg-purple-100 text-purple-700" },
  { value: "NOT_INTERESTED", label: "Not Interested", color: "bg-red-100 text-red-700" },
  { value: "LOST_OPPORTUNITY", label: "Lost Opportunity", color: "bg-red-200 text-red-800" },
  { value: "CONVERTED", label: "Converted to Client", color: "bg-green-100 text-green-700" },
  { value: "ACTIVE", label: "Active", color: "bg-teal-100 text-teal-700" },
];

const DEAL_STATUSES = [
  { value: "OPEN", label: "Open", color: "bg-blue-100 text-blue-700" },
  { value: "NEGOTIATION", label: "Negotiation", color: "bg-orange-100 text-orange-700" },
  { value: "WON", label: "Won", color: "bg-green-100 text-green-700" },
  { value: "LOST", label: "Lost", color: "bg-red-100 text-red-700" },
];

const Badge = ({ status, options }: { status: string; options: any[] }) => {
  const s = options.find(o => o.value === status) || { label: status, color: "bg-gray-100 text-gray-700" };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
};

const CrmCustomer: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showDeal, setShowDeal] = useState(false);
  const [followUpForm, setFollowUpForm] = useState({ date: new Date().toISOString().split("T")[0], comment: "", nextFollowUp: "", updatedStatus: "" });
  const [dealForm, setDealForm] = useState({ dealName: "", dealValue: "", status: "OPEN", expectedCloseDate: "", assignedToId: "" });

  const load = () => api.get(`/crm/${id}`).then(r => setCustomer(r.data));

  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, [id]);

  const submitFollowUp = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/crm/${id}/follow-ups`, followUpForm);
      toast.success("Follow-up added!");
      setShowFollowUp(false);
      setFollowUpForm({ date: new Date().toISOString().split("T")[0], comment: "", nextFollowUp: "", updatedStatus: "" });
      load();
    } catch { toast.error("Failed to add follow-up"); }
  };

  const submitDeal = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/crm/${id}/deals`, dealForm);
      toast.success("Deal added!");
      setShowDeal(false);
      setDealForm({ dealName: "", dealValue: "", status: "OPEN", expectedCloseDate: "", assignedToId: "" });
      load();
    } catch { toast.error("Failed to add deal"); }
  };

  if (!customer) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  const expectedRevenue = customer.deals?.filter((d: any) => d.status === "OPEN" || d.status === "NEGOTIATION").reduce((s: number, d: any) => s + d.dealValue, 0) || 0;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/crm")} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm">
        <ArrowLeft size={16} /> Back to CRM
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Customer Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{customer.customerName}</h2>
                {customer.company && <p className="text-gray-500 mt-0.5">{customer.company}</p>}
              </div>
              <Badge status={customer.status} options={STATUS_OPTIONS} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {customer.phone && <div className="flex items-center gap-2 text-gray-600"><Phone size={14} className="text-gray-400" />{customer.phone}</div>}
              {customer.email && <div className="flex items-center gap-2 text-gray-600"><Mail size={14} className="text-gray-400" />{customer.email}</div>}
              {customer.nextFollowUp && <div className="flex items-center gap-2 text-gray-600"><Calendar size={14} className="text-gray-400" />Next: {new Date(customer.nextFollowUp).toLocaleDateString()}</div>}
              {customer.assignedTo && <div className="flex items-center gap-2 text-gray-600"><User size={14} className="text-gray-400" />{customer.assignedTo.name}</div>}
              {customer.contactMethod && CONTACT_METHODS[customer.contactMethod] && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">Preferred:</span>
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${CONTACT_METHODS[customer.contactMethod].color}`}>
                    {CONTACT_METHODS[customer.contactMethod].icon}
                    {CONTACT_METHODS[customer.contactMethod].label}
                  </span>
                </div>
              )}
            </div>
            {customer.notes && <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">{customer.notes}</div>}
          </div>

          {/* Follow-up Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-gray-800">Customer Follow-up Timeline</h3>
              <button onClick={() => setShowFollowUp(true)} className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700">
                <Plus size={14} /> Add Follow-Up
              </button>
            </div>
            {customer.followUps?.length > 0 ? (
              <div className="space-y-0">
                {customer.followUps.map((f: any, i: number) => (
                  <div key={f.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mt-1 shrink-0" />
                      {i < customer.followUps.length - 1 && <div className="w-0.5 bg-gray-200 flex-1 my-1 min-h-4" />}
                    </div>
                    <div className="pb-5 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-blue-600">{new Date(f.date).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })}</span>
                        {f.updatedStatus && <Badge status={f.updatedStatus} options={STATUS_OPTIONS} />}
                      </div>
                      <p className="text-sm text-gray-700">{f.comment}</p>
                      <div className="flex gap-4 mt-1 text-xs text-gray-400">
                        {f.employee && <span>By: {f.employee.name}</span>}
                        {f.nextFollowUp && <span>Next: {new Date(f.nextFollowUp).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-8">No follow-ups yet. Add the first one!</p>
            )}
          </div>
        </div>

        {/* Deals Sidebar */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 h-fit">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Deals</h3>
            <button onClick={() => setShowDeal(true)} className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
              <Plus size={14} /> Add Deal
            </button>
          </div>
          {expectedRevenue > 0 && (
            <div className="mb-4 p-3 bg-green-50 rounded-lg flex items-center gap-2">
              <DollarSign size={16} className="text-green-600" />
              <div><p className="text-xs text-gray-500">Expected Revenue</p><p className="text-lg font-bold text-green-600">${expectedRevenue.toLocaleString()}</p></div>
            </div>
          )}
          <div className="space-y-3">
            {customer.deals?.length > 0 ? customer.deals.map((d: any) => (
              <div key={d.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800">{d.dealName}</span>
                  <Badge status={d.status} options={DEAL_STATUSES} />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Value</span>
                  <span className="font-semibold">${d.dealValue.toLocaleString()}</span>
                </div>
                {d.expectedCloseDate && <p className="text-xs text-gray-400 mt-1">Close: {new Date(d.expectedCloseDate).toLocaleDateString()}</p>}
                {d.assignedTo && <p className="text-xs text-gray-400">Assigned: {d.assignedTo.name}</p>}
              </div>
            )) : <p className="text-gray-400 text-sm text-center py-6">No deals yet</p>}
          </div>
        </div>
      </div>

      {/* Add Follow-Up Modal */}
      {showFollowUp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Add Follow-Up</h3>
              <button onClick={() => setShowFollowUp(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={submitFollowUp} className="p-6 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" value={followUpForm.date} onChange={e => setFollowUpForm({...followUpForm, date: e.target.value})} required className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Comment *</label>
                <textarea value={followUpForm.comment} onChange={e => setFollowUpForm({...followUpForm, comment: e.target.value})} required rows={3}
                  placeholder="What happened in this follow-up?" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Date</label>
                <input type="date" value={followUpForm.nextFollowUp} onChange={e => setFollowUpForm({...followUpForm, nextFollowUp: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Update Customer Status</label>
                <select value={followUpForm.updatedStatus} onChange={e => setFollowUpForm({...followUpForm, updatedStatus: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">No change</option>
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select></div>
              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowFollowUp(false)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">Save Follow-Up</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Deal Modal */}
      {showDeal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Add Deal</h3>
              <button onClick={() => setShowDeal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={submitDeal} className="p-6 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Deal Name *</label>
                <input value={dealForm.dealName} onChange={e => setDealForm({...dealForm, dealName: e.target.value})} required placeholder="e.g. VPS Hosting Package" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Value ($) *</label>
                  <input type="number" value={dealForm.dealValue} onChange={e => setDealForm({...dealForm, dealValue: e.target.value})} required min={0} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={dealForm.status} onChange={e => setDealForm({...dealForm, status: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                    {DEAL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Expected Close Date</label>
                <input type="date" value={dealForm.expectedCloseDate} onChange={e => setDealForm({...dealForm, expectedCloseDate: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Assigned Employee</label>
                <select value={dealForm.assignedToId} onChange={e => setDealForm({...dealForm, assignedToId: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select employee...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select></div>
              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowDeal(false)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 bg-green-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-700">Add Deal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default CrmCustomer;
